import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × VELÁZQUEZ: Perceptual layers and reflections
// Mirror effects, nested frames, meta-perspective
export function VasarelyVelazquezMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Nested frame structure (Las Meninas meta-perspective) */}
      <g transform="translate(397 540)">
        {/* Multiple nested viewing frames */}
        {Array.from({ length: 8 }).map((_, i) => {
          const size = 260 - i * 30;
          const offset = i * 4;
          return (
            <rect
              key={i}
              x={-size / 2 + offset}
              y={-size / 2 - offset}
              width={size}
              height={size}
              fill="none"
              stroke={i % 3 === 0 ? brand.violet : i % 3 === 1 ? brand.aqua : brand.amber}
              strokeWidth="3"
              opacity={0.6 - i * 0.05}
            />
          );
        })}

        {/* Mirror reflection effect (optical duplication) */}
        {[-1, 1].map((dir) => (
          <g key={dir} opacity="0.4">
            {Array.from({ length: 6 }).map((_, i) => {
              const x = dir * (60 + i * 30);
              const y = -100 + i * 40;
              return (
                <ellipse
                  key={i}
                  cx={x}
                  cy={y}
                  rx="25"
                  ry="35"
                  fill={brand.aqua}
                  opacity={0.2}
                  transform={`skewX(${dir * 15})`}
                />
              );
            })}
          </g>
        ))}

        {/* Perspective grid converging to vanishing point */}
        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i * Math.PI) / 8;
          return (
            <line
              key={i}
              x1="0"
              y1="0"
              x2={Math.cos(angle) * 280}
              y2={Math.sin(angle) * 280}
              stroke={brand.white}
              strokeWidth="1"
              opacity="0.2"
              strokeDasharray="4 4"
            />
          );
        })}

        {/* Observer nodes (meta-perspective markers) */}
        {[
          { x: 0, y: 0, r: 12 },
          { x: -180, y: -120, r: 8 },
          { x: 180, y: -120, r: 8 },
          { x: -180, y: 120, r: 8 },
          { x: 180, y: 120, r: 8 },
        ].map((node, i) => (
          <g key={i}>
            <circle cx={node.x} cy={node.y} r={node.r} fill={brand.violet} opacity="0.6" />
            <circle cx={node.x} cy={node.y} r={node.r + 6} fill="none" stroke={brand.violet} strokeWidth="2" opacity="0.4" />
          </g>
        ))}

        {/* Optical distortion field (Vasarely touch) */}
        {Array.from({ length: 10 }).map((_, i) =>
          Array.from({ length: 10 }).map((_, j) => {
            const x = (i - 4.5) * 45;
            const y = (j - 4.5) * 45;
            const dist = Math.sqrt(x * x + y * y);
            const wave = Math.sin(dist * 0.08) * 6;
            return (
              <circle
                key={`${i}-${j}`}
                cx={x + wave}
                cy={y - wave}
                r="4"
                fill={brand.amber}
                opacity={0.15 + Math.sin(i * 0.5 + j * 0.5) * 0.1}
              />
            );
          })
        )}

        {/* Frame within frame markers */}
        <rect x="-240" y="-200" width="480" height="400" fill="none" stroke={brand.white} strokeWidth="4" opacity="0.5" />
        <rect x="-220" y="-180" width="440" height="360" fill="none" stroke={brand.aqua} strokeWidth="2" opacity="0.4" />
      </g>

      {/* Meta-commentary text */}
      <text x="397" y="980" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.white} opacity="0.5" fontStyle="italic">
        el espejo y el observador
      </text>
    </g>
  );
}
